import React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { ListItemText, MenuItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const defaultFilterOptions = createFilterOptions();

/**
 * MUI Autocomplete compares options with `isOptionEqualToValue`. Rows whose `value`
 * is `''` (e.g. “No user” sentinel) must compare by `.value`, not truthiness on the
 * whole option object.
 *
 * @param {{ value: unknown, label?: unknown }|null|undefined} option
 * @param {{ value: unknown, label?: unknown }|null|undefined} value
 * @returns {boolean}
 */
function optionValuesAreEqual(option, value) {
  if (option == null && value == null) return true;
  if (option == null || value == null) return false;
  return String(option.value) === String(value.value);
}

/**
 * Whether an option uses the empty-string sentinel (`No user`, etc.).
 * Matches {@link findUserOption} semantics (`String(value ?? '') === ''`).
 *
 * @param {{ value: unknown }|null|undefined} option
 * @returns {boolean}
 */
function isEmptyValueSentinelOption(option) {
  if (option == null) return false;
  return String(option.value ?? '') === '';
}

/**
 * Keeps helper-prepended empty-value options visible while preserving MUI's
 * default text filtering for normal options.
 *
 * Important: {@link defaultFilterOptions} is applied only to **non-sentinel**
 * rows. Running it on the full list can drop or mishandle `value: ''` rows
 * depending on MUI version / stringify behavior.
 *
 * @param {Array<{ value: unknown, label?: unknown }>} options
 * @param {Object} params - MUI `FilterOptionsState`
 * @returns {Array<{ value: unknown, label?: unknown }>}
 */
function filterOptionsWithSentinel(options, params) {
  const sentinelOptions = options.filter(isEmptyValueSentinelOption);
  const nonSentinelOptions = options.filter(o => !isEmptyValueSentinelOption(o));
  const filteredNonSentinels = defaultFilterOptions(nonSentinelOptions, params);

  if (!sentinelOptions.length) {
    return filteredNonSentinels;
  }

  return [...sentinelOptions, ...filteredNonSentinels];
}

/**
 * Renders a dropdown select component with autocomplete functionality.
 *
 * @param {Object} props - The required props for the DropdownSelect component.
 * @param {Array} props.optionsList - An array of options to populate the dropdown list.
 * @param {string} props.label - The label for the dropdown select component.
 * @param {any} props.selectedValue - The currently selected value in the dropdown.
 * @param {function} props.setSelectedValue - Callback function to update the selected value.
 * @param {Array} props.disabledOptions - An array of values that should be disabled in the dropdown select.
 * @param {boolean} props.touched - Indicates whether the dropdown select has been interacted with.
 * @param {string} props.error - Error message to display for validation errors.
 * @param {boolean} props.isInvalid - Indicates whether the dropdown select has a validation error.
 * @param {function} props.onBlur - Callback function to handle onBlur event.
 * @param {boolean} [props.preserveEmptyValueOptions=true] - When true, options whose
 *   `value` stringifies to empty (sentinel rows like “No user”) stay in the dropdown and
 *   bypass broken filtering on `value: ''`. Set false only for rare lists that
 *   intentionally use empty values as normal filtered options.
 * @param {boolean} [props.clearable=false] - When true, the clear (×) control is shown so
 *   the selection can be cleared (`null` passed to `setSelectedValue`).
 * @param {Function} [props.filterOptions] - Custom Autocomplete filter
 *   `(options, params) => options`. When set, replaces the default text filter
 *   (sentinel / Not-placed handling is left to the caller).
 * @returns {HTMLElement} - The rendered DropdownSelect component.
 */
const DropdownSelect = ({
  optionsList,
  label,
  placeholder,
  selectedValue,
  setSelectedValue,
  disabledOptions = [],
  touched,
  error,
  isInvalid,
  onBlur,
  disabled,
  groupBy, // Optional function to group options (e.g., by supplier)
  preserveEmptyValueOptions = true,
  clearable = false,
  filterOptions: customFilterOptions,
}) => {
  const normalizedOptions = Array.isArray(optionsList) ? optionsList : [];
  const selectedLabel =
    selectedValue?.label != null ? String(selectedValue.label) : '';

  return (
    <Autocomplete
      selectOnFocus
      popupIcon={<ExpandMoreIcon />}
      id="dropdown_select" // Unique identifier for the Autocomplete input
      options={normalizedOptions} // Array of options to populate dropdown list
      filterOptions={(options, params) => {
        // Opening the list fills the input with the current label. Filtering
        // on that text hides shorter places, such as a shelf under a box.
        if (selectedLabel && params.inputValue === selectedLabel) {
          return options;
        }
        if (typeof customFilterOptions === 'function') {
          return customFilterOptions(options, params);
        }
        return preserveEmptyValueOptions
          ? filterOptionsWithSentinel(options, params)
          : defaultFilterOptions(options, params);
      }}
      sx={{ width: '100%' }} // Defines the width of the text - responsive
      componentsProps={{
        popper: {
          sx: { zIndex: 1300 },
        },
      }}
      value={selectedValue !== undefined ? selectedValue : null} // The currently selected value in the DropdownSelect
      isOptionEqualToValue={optionValuesAreEqual}
      getOptionLabel={option =>
        option == null ? '' : String(option.label ?? '')
      }
      onChange={(event, newValue) => {
        setSelectedValue(newValue != null ? newValue : null);
      }} // Event handler to update the selected value in the parent component when an option is selected in the dropdown
      disabled={disabled}
      groupBy={groupBy} // Group options by supplier if provided
      renderInput={params => (
        <TextField
          {...params}
          label={label} // This will appear when focused or filled
          placeholder={placeholder} // This shows when nothing is selected
          error={touched && Boolean(isInvalid)}
          helperText={isInvalid && error}
          onBlur={onBlur}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '0.375rem',
              fontSize: '1rem',
              height: '2.4rem',
              paddingRight: '0.5rem',
              '& input': {
                padding: '0.5rem 0.75rem', // vertical & horizontal padding
              },
              '& fieldset': {
                borderColor: '#ced4da',
              },
              '&:hover fieldset': {
                borderColor: '#86b7fe',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#86b7fe',
                borderWidth: '0.15rem',
              },
            },
            '& .MuiInputLabel-root': {
              top: '-6px', // 🛠 adjust this value to lift label upward
              fontSize: '0.875rem',
            },
            '& .MuiInputLabel-shrink': {
              top: '0', // reset when floating
            },
            '& .MuiFormHelperText-root': {
              marginLeft: 0,
            },
          }}
        />
      )} // Callback used to display TextField component
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <MenuItem
            key={key}
            {...rest}
            disabled={disabledOptions.includes(option.value)}
          >
            <ListItemText primary={option.label} />
          </MenuItem>
        );
      }}
      // Callback used to customize how options are rendered in the dropdown menu
      disableClearable={!clearable}
    />
  );
};
export default DropdownSelect;
